/**
 * Payment provider connections (ADR 0032): the per-company authorisation a provider such as
 * BTG Pactual needs before Alchemy ERP can send payments from that company's accounts. The
 * HTTP contract of `integrations/btg/*`; secrets never travel in any of these shapes.
 */

import type {
  DdaSubscriptionVendorStatus,
  PaymentProviderConnectionStatus,
} from './generated/enums.js';

/**
 * Why a connection lapsed. `REFRESH_GRANT_REFUSED`: the provider refused the refresh token
 * (consent revoked or expired). `REFRESH_FAILING_TEN_DAYS`: refreshes kept failing for ten
 * days, the refresh token's own life. `PROVIDER_REFUSED_TOKEN`: a Banking call answered 401 or
 * 403 with the current token.
 */
export const PAYMENT_PROVIDER_CONNECTION_LAPSE_REASONS = [
  'REFRESH_GRANT_REFUSED',
  'REFRESH_FAILING_TEN_DAYS',
  'PROVIDER_REFUSED_TOKEN',
] as const;
export type PaymentProviderConnectionLapseReason =
  (typeof PAYMENT_PROVIDER_CONNECTION_LAPSE_REASONS)[number];

/** Provider id of BTG Pactual Empresas in connections, capabilities and attempts. */
export const BTG_PROVIDER_ID = 'btg' as const;

/** COMPE code of Banco BTG Pactual: an origin account with it sends through the BTG connection. */
export const BTG_BANK_CODE = '208' as const;

/**
 * Result of the OAuth callback, carried back to the web page as `?btgConnection=<code>`. Only
 * `connected` stored anything.
 */
export const BTG_CONNECTION_RESULT_CODES = [
  'connected',
  /** The state and the context cookie were both missing, forged, expired or already used. */
  'state_invalid',
  /** The user refused the consent at BTG Id, or BTG Id answered an OAuth error. */
  'consent_denied',
  /** The consented company is not the entity's CNPJ; nothing was stored. */
  'cnpj_mismatch',
  /** The tokens carry no company, or more than one. */
  'company_unresolved',
  /** The entity has no CNPJ to compare against. */
  'entity_cnpj_missing',
  /** BTG Id refused the code exchange, or could not be reached. */
  'exchange_failed',
  /** The BTG app is not configured in this environment. */
  'not_configured',
] as const;
export type BtgConnectionResultCode = (typeof BTG_CONNECTION_RESULT_CODES)[number];

export const BTG_CONNECTION_RESULT_QUERY_PARAM = 'btgConnection';

/** `POST integrations/btg/connections/:entityId/start`. */
export interface BtgConnectionStartResponse {
  /** Where the browser goes: BTG Id's authorization endpoint for this consent. */
  authorizationUrl: string;
  /** ISO-8601; after it the callback refuses this consent. */
  expiresAt: string;
}

/**
 * One account of a connected company, as `GET integrations/btg/connections` returns it.
 * Branch and number keep the bank's spelling, so the register link can prefill them.
 */
export interface BtgConnectionAccountItem {
  /**
   * BTG's `accountId` when the list was read from the bank. On a lapsed connection the
   * token cannot be used, so this is `local:<bankAccountId>` and is not an id BTG accepts.
   */
  accountId: string;
  branchCode: string;
  number: string;
  /** What BTG calls the account (`CHECKING`, `SAVINGS`, `PAYMENT`, …), or null when it does not say. */
  type: string | null;
  /**
   * The registered bank account linked to this BTG account, or else the one that matches by
   * branch and number (it is linked on this read); null when none does.
   */
  bankAccountId: string | null;
  bankAccountName: string | null;
  /** The matched account's payment provider is BTG. False when nothing matches. */
  sendsThroughApi: boolean;
  /** The matched account has outbound payments on, through its provider. False when nothing matches. */
  paymentsEnabled: boolean;
  /** The matched account's payment setup, or null when nothing matches. */
  paymentSetupStatus: 'DISABLED' | 'CONFIGURING' | 'READY' | 'ERROR' | null;
  /**
   * The matched account is ACTIVE and linked to this BTG account: what its "Pagamentos pela
   * API" switch (`PUT integrations/btg/accounts/:bankAccountId/modules`) acts on.
   */
  apiPaymentsSwitchable: boolean;
}

/**
 * `PUT integrations/btg/accounts/:bankAccountId/modules`: the modules of one bank account linked
 * to a BTG account. A field left out stays as it is; at least one is given.
 */
export interface SetBtgAccountModulesRequest {
  /**
   * "Pagamentos pela API": true moves the account to the BTG provider and enables its payments
   * (needs an ACTIVE connection); false disables its payments while it sends through BTG.
   */
  apiPayments?: boolean;
}

/**
 * The account's modules after the change, in the fields of {@link BtgConnectionAccountItem}, so
 * the card can show it without reading the bank again.
 */
export interface BtgAccountModulesResponse {
  bankAccountId: string;
  /** The account's payment provider is BTG. */
  sendsThroughApi: boolean;
  /** The account has outbound payments on, through its provider. */
  paymentsEnabled: boolean;
  paymentSetupStatus: 'DISABLED' | 'CONFIGURING' | 'READY' | 'ERROR';
}

/**
 * Refusals of `PUT integrations/btg/accounts/:bankAccountId/modules`, with their HTTP status. The
 * body is `{ code, message }`. A provider switch a send in flight holds answers the payment
 * setup's `PROVIDER_SWITCH_BLOCKED` (409).
 */
export const BTG_ACCOUNT_MODULES_ERROR_CODES = {
  /** The body names no module to change. */
  BTG_ACCOUNT_MODULES_EMPTY: 400,
  /** The account is not an ACTIVE account linked to a BTG account of its company. */
  BTG_ACCOUNT_NOT_LINKED: 409,
  /** Turning API payments on needs the company's BTG connection ACTIVE. */
  BTG_CONNECTION_NOT_ACTIVE: 409,
  /** Another provider was chosen for the account meanwhile; it was left as it is. */
  PROVIDER_CHANGED: 409,
} as const satisfies Record<string, number>;
export type BtgAccountModulesErrorCode = keyof typeof BTG_ACCOUNT_MODULES_ERROR_CODES;

/** One legal entity on the BTG card, with its current connection if any. */
export interface BtgConnectionEntityItem {
  entityId: string;
  entityName: string;
  /** The entity's CNPJ, as registered; the consent must name the same company. */
  cnpj: string | null;
  /** Active BTG bank accounts of the entity (COMPE 208) that this caller can see. */
  btgBankAccountCount: number;
  connection: BtgConnectionSummary | null;
  /**
   * Present when the connection is ACTIVE or LAPSED. ACTIVE is the live
   * `GET /{companyId}/banking/accounts` list, each row matched to a registered
   * account of the same entity that the caller can see. A bank row that
   * matches only a registered account outside that scope is omitted; a row
   * that matches nothing stays, so it can be registered. LAPSED is only the
   * registered accounts the caller can see (the token cannot be read). A bank
   * read whose token refresh lapses the connection is answered as LAPSED.
   * An account that exists only at the bank shows up again after reconnect.
   */
  accounts?: BtgConnectionAccountItem[];
  /**
   * True when an ACTIVE connection's list could not be read. `accounts` is then
   * empty, which is not the same as the bank returning no accounts.
   */
  accountsUnavailable?: boolean;
}

/** A connection without its secrets. */
export interface BtgConnectionSummary {
  connectionId: string;
  status: PaymentProviderConnectionStatus;
  externalCompanyId: string;
  scopesGranted: string[];
  /**
   * Scopes the current app asks for that this connection was not granted. Diagnostics only:
   * BTG may leave out scopes no module uses, so the card decides per module from
   * `scopesGranted`, never from this.
   */
  missingScopes: string[];
  /** True when `missingScopes` is not empty. Diagnostics only, as `missingScopes`. */
  needsReconsent: boolean;
  consentedByUserId: string | null;
  /** ISO-8601 timestamps, or null. */
  consentedAt: string | null;
  lastRefreshAt: string | null;
  accessTokenExpiresAt: string | null;
  refreshFailingSince: string | null;
  lapsedAt: string | null;
  lapseReason: PaymentProviderConnectionLapseReason | null;
  /** Stable code of the last refresh or exchange failure, for support. */
  lastErrorCode: string | null;
}

/** `GET integrations/btg/connections`. */
export interface BtgConnectionListResponse {
  /** Whether the BTG app is configured in this environment (client id, secret, redirect). */
  configured: boolean;
  items: BtgConnectionEntityItem[];
}

/** `GET integrations/btg/connections/entities/:entityId/status`: what a bank account shows. */
export interface BtgEntityConnectionStatusResponse {
  entityId: string;
  status: PaymentProviderConnectionStatus | 'NOT_CONNECTED';
  lapsedAt: string | null;
  lapseReason: PaymentProviderConnectionLapseReason | null;
}

/**
 * Refusals of `POST integrations/btg/connections/:connectionId/webhook` (an admin route), with
 * their HTTP status. The body is `{ code, message }`.
 */
export const BTG_WEBHOOK_ERROR_CODES = {
  /** The BTG app (client id and secret) is not configured in this environment. */
  BTG_NOT_CONFIGURED: 503,
  /** `PUBLIC_API_BASE_URL` is not set: there is no address BTG could deliver to. */
  BTG_WEBHOOK_URL_NOT_CONFIGURED: 409,
  /** No BTG connection with this id in the tenant. */
  BTG_CONNECTION_NOT_FOUND: 404,
  /** The connection is lapsed, revoked or pending: reconnect it first. */
  BTG_CONNECTION_NOT_ACTIVE: 409,
  /** BTG Id or the webhook API refused or could not be reached; nothing was changed at BTG. */
  BTG_WEBHOOK_UNAVAILABLE: 502,
} as const satisfies Record<string, number>;
export type BtgWebhookErrorCode = keyof typeof BTG_WEBHOOK_ERROR_CODES;

/**
 * What registering the webhook did at BTG. `UNCHANGED`: a webhook for our address already
 * carried the payment events. `PATCHED`: one of ours was pointed at the address or given the
 * payment events. `CREATED`: a new one was created. `PORTAL_REQUIRED`: none exists and the API
 * cannot create one; an admin registers it in the BTG portal and runs the action again. For a
 * public app (no client secret) it is the only answer: the webhook API is unreachable.
 */
export type BtgWebhookRegistrationAction = 'UNCHANGED' | 'PATCHED' | 'CREATED' | 'PORTAL_REQUIRED';

/** `POST integrations/btg/connections/:connectionId/webhook`. Never carries a secret. */
export interface BtgWebhookRegistrationResponse {
  connectionId: string;
  /** The inbox registration deliveries are verified against (bearer secret). */
  registrationId: string;
  /** The address BTG must deliver to. */
  endpoint: string;
  action: BtgWebhookRegistrationAction;
  /** BTG's id of the webhook, when one exists for the address. */
  vendorWebhookId: string | null;
  events: string[];
  /** Whether the verification secret now on the registration is the one BTG holds. */
  secretSynced: boolean;
  /** BTG reports the webhook disabled: it delivers nothing until enabled in the portal. */
  vendorDisabled: boolean;
  /** Earlier registrations of the connection disabled by this one. */
  replacedRegistrations: number;
  /** For `PORTAL_REQUIRED`: what the admin does in the BTG portal (admin copy, pt-BR). */
  instructions: string | null;
}

/**
 * A company's opt-in to pull boletos charged to it (DDA) through one provider. Nothing is
 * pulled while `enabled` is false. `vendorStatus` is written by the pull, never by hand.
 */
export interface DdaSubscriptionSummary {
  entityId: string;
  entityName: string | null;
  providerId: string;
  enabled: boolean;
  /** BTG connection id, or the TecnoSpeed account that holds the adhesion letter. */
  channelRef: string | null;
  vendorStatus: DdaSubscriptionVendorStatus;
  /** ISO-8601 timestamps, or null. */
  requestedAt: string | null;
  confirmedAt: string | null;
  lastSyncAt: string | null;
  /** User-safe reason of the last failed pull (pt-BR), or null. */
  lastError: string | null;
}

/** `GET integrations/dda/subscriptions`. */
export interface DdaSubscriptionListResponse {
  items: DdaSubscriptionSummary[];
}

/** Message of a company that did not opt in, shown where a pull skipped it. */
export const DDA_NOT_SUBSCRIBED_REASON = 'A empresa não aderiu à consulta de débitos (DDA).';

/** Providers a company can opt in to for DDA: the ones that can read boletos charged to it. */
export const DDA_SUBSCRIPTION_PROVIDER_IDS = ['btg', 'tecnospeed'] as const;
export type DdaSubscriptionProviderId = (typeof DDA_SUBSCRIPTION_PROVIDER_IDS)[number];
